// WHAT: Oura-Activity-Komponente, die Tageskalorienziel, tatsächliche Aktivkalorien und Sedentary Time visualisiert
// WHY:  Hebt Fortschritt gegenüber dem Aktivitätsziel hervor und macht Sitzzeiten sowie Activity Score transparent
// DOMAIN: health → oura → molecules
// DEPENDENCIES: TracQ `Card`, `HalfCircleProgress`, optimierte Oura-Aktivitäts-Hooks (liefert Props)
// RISKS:
//   - NOTE: Erwartet kalorienbasierte Targets (`baseTarget`) und Sedentary Time in Sekunden; falsche Einheiten verzerren die Anzeige
import { Card } from "@/components/ui/Card";

import { HalfCircleProgress } from "../ui/HalfCircleProgress";

interface ActivityGoalProperties {
  activeCalories: number;
  activityScore: number;
  baseTarget: number;
  sedentaryTime: number; // in Sekunden
}

/**
 * WHAT:
 *   Formats seconds into a human-readable time string (hours and minutes).
 *
 * WHY:
 *   Provides user-friendly time display for sedentary time.
 *
 * @param sec - Time in seconds
 *
 * @returns {string} Formatted time string (e.g., "2h 30min")
 *
 */
function formatTime(sec: number) {
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  return `${String(h)}h ${String(m)}min`;
}

/**
 * WHAT:
 *   Component displaying activity goal progress with calories, target, and sedentary time.
 *
 * WHY:
 *   Provides visual feedback on daily activity goals and progress.
 *
 * @param root0 - Component props
 *
 * @param root0.activeCalories - Active calories burned
 *
 * @param root0.activityScore - Activity score
 *
 * @param root0.baseTarget - Base target calories
 *
 * @param root0.sedentaryTime - Sedentary time in seconds
 *
 * @returns {JSX.Element} Activity goal component
 *
 */
export const ActivityGoal = ({
  activeCalories,
  activityScore,
  baseTarget,
  sedentaryTime,
}: Readonly<ActivityGoalProperties>) => {
  /**
   * WHAT:
   *   Berechnet den prozentualen Fortschritt gegenüber dem Basisziel (`baseTarget`) und
   *   leitet daraus Übererfüllung sowie Anzeige-Text ab.
   *
   * BEHAVIOR:
   *   - `percent` wird auf Ganzzahlen gerundet; Werte > 100% werden in `overAchieved` gespiegelt.
   */
  const percent = baseTarget > 0 ? Math.round((activeCalories / baseTarget) * 100) : 0;
  const overAchieved = percent > 100 ? percent - 100 : 0;

  return (
    <Card className="text-center" padding="lg" variant="default">
      <h2 className="text-lg font-bold tracking-wide mb-3 text-neutral-200">ACTIVITY GOAL</h2>

      <div className="flex justify-center mb-2">
        <HalfCircleProgress
          bgColor="#fff"
          color="#4ade80"
          percent={percent}
          size={180}
          strokeWidth={10}
        />
      </div>

      <div className="text-5xl font-bold mb-1 text-neutral-100">{activeCalories}</div>
      <div className="text-lg font-semibold mb-1 text-neutral-200">
        Target Calories <span className="text-neutral-400 font-normal">{baseTarget}</span>
      </div>
      <div className="text-base mb-3 text-neutral-300">
        {percent >= 100 ? (
          <span>
            You exceeded your goal by{" "}
            <span className="text-green-400 font-bold">{overAchieved}%</span>!
          </span>
        ) : (
          <span>{percent}% of your goal achieved.</span>
        )}
      </div>

      <div className="flex justify-around text-xs pt-4 border-t border-neutral-700 mt-4">
        <div className="flex flex-col items-center">
          <span className="mb-1 text-neutral-400">Base Target</span>
          <span className="text-lg font-semibold text-neutral-200">{baseTarget}</span>
        </div>
        <div className="flex flex-col items-center">
          <span className="mb-1 text-neutral-400">Sedentary Time</span>
          <span className="text-lg font-semibold text-neutral-200">
            {formatTime(sedentaryTime)}
          </span>
        </div>
        <div className="flex flex-col items-center">
          <span className="mb-1 text-neutral-400">Activity Score</span>
          <span className="text-lg font-semibold text-neutral-200">
            {activityScore}{" "}
            <span aria-label="crown" role="img">
              👑
            </span>
          </span>
        </div>
      </div>
    </Card>
  );
};
